import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavbar from "examples/Navbars/TopNavbar";
import Footer from "layouts/pages/LandingPage/components/Footer";
import RealtimeGauge from "components/RealtimeGauge";
import HourlyChart from "components/HourlyChart";
import CurrentClass from "components/CurrentClass"
import LocationCard from "components/LocationCard";
import CommentsCard from "components/CommentsCard"
import StatusCard from "components/StatusCard";
import EventTable from "components/EventTable";
import {useAuth} from "context/AuthContext";
import { MqttProvider } from "context/MQTTContext";

interface LocationData {
    id: number;
    location_name: string;
    schedule_url: string;
    created_at: string;
    sensorid: string;
    timezone: string;
    street_address1: string;
    streetaddress2: string | null;
    city: string;
    state: string;
    zipcode: string;
    latitude: string;
    longitude: string;
    trigger_level: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const ExampleLayout: React.FC = () => {
    const { locationID } = useParams();
    const totalHeight = 300; // Total height for the top row (on medium and larger screens)
    const gap = 16; // Gap between left cards in pixels
    // For the left column, each stacked card should be half the total height minus half the gap,
    // but on small screens, we'll let the height be auto.
    const leftRowHeight = { xs: "auto", md: `calc((100% - ${gap}px) / 2)` };

    // temporary hard-code location
    const locationName = 'Chestnut Hill';

    const [locationData, setLocationData] = useState<LocationData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    const { sessionToken } = useAuth();

    useEffect(() => {
        if (!sessionToken) {

            return;  // Prevents call if sessionToken is null
        }


        const fetchLocation = async () => {
            try {
                setLoading(true); // Set loading state when fetch starts

                const response = await fetch(`${API_BASE_URL}/db/locations?locationID=${encodeURIComponent(locationID)}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    throw new Error("Error fetching location data");
                }
                const data: LocationData[] = await response.json();

                if (data && data.length > 0) {
                    setLocationData(data[0] as LocationData);  // Store just the first object
                }
            } catch (err: any) {
                setError(err.message || "Unknown error");
            } finally {
                setLoading(false);
            }
        };

        fetchLocation();
    }, [locationName, locationID, sessionToken]);


    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!locationData) {
        return <div>No location data available</div>;
    }
        return (
        <DashboardLayout>
            <TopNavbar />
            <MDBox mt={4} mb={4} mx={3}>
                {/* Top Row */}
                <Grid
                    container
                    spacing={2}
                    alignItems="stretch"
                    sx={{ height: { xs: "auto", md: totalHeight } }}
                >
                    {/* Left Column: Two stacked cards */}
                    <Grid item xs={12} md={8}>
                        <MDBox sx={{ height: "100%" }}>
                            {/* Top row split into two cards horizontally in a 5:3 ratio */}
                            <MDBox display="flex" gap={`${gap}px`} sx={{ height: leftRowHeight }}>
                                <Card sx={{ flex: 5, height: "100%" }}>
                                    <LocationCard location={locationData} />
                                </Card>
                                <Card sx={{ flex: 3, height: "100%" }}>
                                    <StatusCard status="Online"/>
                                </Card>
                            </MDBox>
                            {/* Bottom row: Current Class */}
                            <MDBox mt={`${gap}px`} sx={{ height: leftRowHeight }}>
                                <Card sx={{ height: "100%" }}>
                                    <CurrentClass locationName={locationData?.location_name} />
                                </Card>
                            </MDBox>
                        </MDBox>
                    </Grid>
                    {/* Right Column: Realtime Gauge */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: { xs: "auto", md: totalHeight }, alignItems: "center", justifyContent: "center" }}>
                            <MqttProvider topicPrefix={locationData.sensorid}>
                                <RealtimeGauge topicPrefix={locationData.sensorid}/>
                            </MqttProvider>
                        </Card>
                    </Grid>
                </Grid>

                {/* Additional Rows: Two full-width containers */}
                <MDBox mt={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {/* ... */}
                            <HourlyChart location_id={locationData?.id} timezone={locationData?.timezone} threshold={locationData?.trigger_level}/>
                            {/* ... */}
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <Card>
                                        <MDBox p={2}>
                                            <MDTypography variant="h5">High Noise Events</MDTypography>
                                            <MDTypography variant="h6">Current Threshold = {locationData.trigger_level}dBA</MDTypography>
                                            <EventTable location_id={locationData?.id} timezone={locationData?.timezone} />
                                        </MDBox>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={5} mb={5}>
                                            <CommentsCard location_id={locationData?.id} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </MDBox>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
};

export default ExampleLayout;
